/**
 * Compares two arrays for equality by checking length and element-by-element comparison.
 *
 * @param {string[]} a - The first array to compare.
 * @param {string[]} b - The second array to compare.
 * @returns {boolean} True if arrays have the same length and identical elements in the same order, false otherwise.
 *
 * @example
 * arraysEqual(['a', 'b'], ['a', 'b']); // true
 * arraysEqual(['a', 'b'], ['b', 'a']); // false (different order)
 * arraysEqual(['a'], ['a', 'b']); // false (different length)
 * arraysEqual([], []); // true
 */
export const arraysEqual = (a: string[], b: string[]): boolean => {
  return a.length === b.length && a.every((val, index) => val === b[index]);
};

/**
 * Compares two arrays for equality regardless of element order.
 *
 * @param {string[]} a - The first array to compare.
 * @param {string[]} b - The second array to compare.
 * @returns {boolean} True if arrays contain the same elements (order-insensitive), false otherwise.
 *
 * @example
 * arraysEqualUnordered(['a', 'b'], ['b', 'a']);  // true
 * arraysEqualUnordered(['a', 'b'], ['a', 'b']);  // true
 * arraysEqualUnordered(['a'], ['a', 'b']);       // false
 */
export const arraysEqualUnordered = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
};
